import React, { useState } from 'react';
import { MUSIC_ENABLED_KEY, SOUNDS_ENABLED_KEY } from '@root/constants';
import { useAutoRollCountdown } from '@root/hooks';
import { gameAudio } from '@root/lib';
import { appSelector, type AppSliceState } from '@root/store/slices/appSlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import { useImageUrl } from '@shared/components/ImageContext';
import { useAnalytics } from '@shared/hooks';
import { AnalyticsEvent } from '@shared/services/analytics';
import { useTranslation } from 'react-i18next';
import styles from './MenuPopup.module.scss';

const readFlag = (key: string): boolean =>
  localStorage.getItem(key) !== 'false';

const MenuPopup: React.FC = () => {
  const { t } = useTranslation();
  const { menuPopupOpen } = useTypedSelector<AppSliceState>(appSelector);
  const { round } = useTypedSelector(roundSelector);
  const {
    setMenuPopupOpen,
    setGameLimitsModalOpen,
    setMyBetsHistoryModalOpen,
    setGameRulesModalOpen,
    setGameInfoModalOpen,
  } = useActions();
  const { trackPage } = useAnalytics();
  const { img, imgProps } = useImageUrl();
  const seconds = useAutoRollCountdown(
    round?.state?.rollDeadline,
    menuPopupOpen
  );

  const [musicEnabled, setMusicEnabled] = useState(() =>
    readFlag(MUSIC_ENABLED_KEY)
  );
  const [soundsEnabled, setSoundsEnabled] = useState(() =>
    readFlag(SOUNDS_ENABLED_KEY)
  );

  if (!menuPopupOpen) return null;

  const skin = {
    '--button-skin': `url(${img('ButtonSkin_Secondary')})`,
  } as React.CSSProperties;

  const close = () => {
    trackPage(AnalyticsEvent.INSTANT_HAMBURGER_CLOSED);
    setMenuPopupOpen(false);
  };

  // Each entry closes the menu and opens its own dialog.
  const go = (event: AnalyticsEvent, show: (shown: boolean) => void) => {
    trackPage(event);
    setMenuPopupOpen(false);
    show(true);
  };

  const toggleMusic = () => {
    const next = !musicEnabled;
    setMusicEnabled(next);
    localStorage.setItem(MUSIC_ENABLED_KEY, String(next));
    gameAudio.setMusicEnabled(next);
  };

  const toggleSounds = () => {
    const next = !soundsEnabled;
    setSoundsEnabled(next);
    localStorage.setItem(SOUNDS_ENABLED_KEY, String(next));
    gameAudio.setSoundEnabled(next);
  };

  const entries: [string, () => void][] = [
    [
      t('How to play'),
      () => go(AnalyticsEvent.INSTANT_GAME_RULES_OPENED, setGameRulesModalOpen),
    ],
    [
      t('Game limits'),
      () =>
        go(AnalyticsEvent.INSTANT_GAME_LIMITS_OPENED, setGameLimitsModalOpen),
    ],
    [
      t('Player history'),
      () =>
        go(AnalyticsEvent.HISTORY_BUTTON_CLICKED, setMyBetsHistoryModalOpen),
    ],
    [
      t('Game info'),
      () => go(AnalyticsEvent.RULES_BUTTON_CLICKED, setGameInfoModalOpen),
    ],
  ];

  return (
    <div className={styles.scrim}>
      <div
        className={styles.dialog}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{t('Menu')}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={close}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>

        {entries.map(([label, onClick]) => (
          <button
            key={label}
            type="button"
            className={styles.entry}
            style={skin}
            onClick={onClick}
          >
            {label}
          </button>
        ))}

        <div className={styles.audio}>
          <p className={styles.group}>{t('Audio')}</p>
          <button
            type="button"
            className={styles.entry}
            style={skin}
            onClick={toggleMusic}
          >
            {musicEnabled ? t('Music: On') : t('Music: Off')}
          </button>
          <button
            type="button"
            className={styles.entry}
            style={skin}
            onClick={toggleSounds}
          >
            {soundsEnabled ? t('Sound effects: On') : t('Sound effects: Off')}
          </button>
        </div>

        {seconds > 0 && (
          <p className={styles.active}>
            {t('Active round · auto-roll in {{seconds}}s', { seconds })}
          </p>
        )}
      </div>
    </div>
  );
};

export default MenuPopup;
